import styled from '@emotion/styled'
import * as React from 'react'

import { TContrastLevel } from '~theme/typings'
import { Icon } from '~ui/components/icons/Icon'
import { IconMessage } from '~ui/components/icons/specific/IconMessage'
import { Box, Grid } from '~ui/components/layout/Box'
import { Stack } from '~ui/components/layout/Stack'
import { Image } from '~ui/components/media/Image/Image'
import { H1 } from '~ui/components/typography/Heading/Heading'
import { Text } from '~ui/components/typography/Text/Text'
import { cn } from '~ui/libs/classnames'
import { letterSpacingValues } from '~ui/styleGuide/fontStyles'
import { getSpacingValue } from '~ui/styleGuide/spaces'
import { breakpoints } from '~ui/styleGuide/styles'
import { TRespFontByWithOptions } from '~ui/utils/responsiveFonts'

// different layouts that the component data can be presented in
type THeaderLayout = 'desktop' | 'compact' | 'mobile'

type TFontSizeBreakpoints = Pick<TRespFontByWithOptions, 'maxBreakpoint' | 'minBreakpoint'>
type TFontSizeRange = Pick<TRespFontByWithOptions, 'maxSize' | 'minSize'>

type TCompany = {
  /** nameLabel of the brand or the branch */
  companyNameLabel: string
  /** company description or a message */
  description?: Nullable<string>
  /** company logo url */
  logoUrl?: string
  /**
   * is the description a promo header text?
   * it is used to display the icon next to the promo header text - to highlight it
   */
  isPromoHeaderText?: boolean
}

type TProps = NoChildren & {
  /**
   * layout type that the company info should be rendered in (should reflect the content and viewport)
   */
  layout?: THeaderLayout
  /**
   * top line that may contain language picker, back button, current information...
   */
  topLine: React.ReactNode
  /**
   * company data to be rendered
   */
  company: TCompany
  /**
   * any image element such as next/image, HTML img element or other,
   * if no such element is provided, the chakra image is used with the item's imageUrl
   */
  renderImageElement?: (company: TCompany) => React.ReactNode
  /**
   * object with maxBreakpoint and minBreakpoint key-value pairs,
   * that are used by the component to calculate font sizes accordingly
   */
  responsiveBreakpoints?: TFontSizeBreakpoints
  /**
   * object with maxSize and minSize key-value pairs,
   * that specify the main heading fontSize for maxBreakpoint and minBreakpoint respectively
   */
  headingFontSizeRange?: TFontSizeRange
  /**
   * object with maxSize and minSize key-value pairs,
   * that specify the description text fontSize for maxBreakpoint and minBreakpoint respectively
   */
  descriptionFontSizeRange?: TFontSizeRange
  /**
   * unit used within the responsive sizes calculations, the default value is px
   */
  responsiveUnit?: TRespFontByWithOptions['unit']
}

// TODO make the logo dimensions more responsive?

export const StyledLogoBox = styled(Box)`
  --logo-dimensions: 80px;

  &.small {
    --logo-dimensions: 60px;
    margin-bottom: ${getSpacingValue(`none`)};
  }

  height: var(--logo-dimensions);
  width: var(--logo-dimensions);

  overflow: hidden;

  img {
    height: var(--logo-dimensions);
    width: var(--logo-dimensions);
    object-fit: cover;
  }
`

const FG_CONTRAST: TContrastLevel = `high`

const defaultBreakpoints: TFontSizeBreakpoints = {
  maxBreakpoint: breakpoints.lg,
  minBreakpoint: breakpoints.sm,
}

const defaultHeadingSizes: TFontSizeRange = {
  minSize: 22,
  maxSize: 50,
}

const defaultDescriptionSizes: TFontSizeRange = {
  minSize: 14,
  maxSize: 18,
}

/**
 * renders main info about the company (name, logo, description) in three possible layouts
 * ! the hero image (that this is typically rendered on) is not part of this component and it should be added behind it for perfect positioning
 */
export const CompanyHero: React.FC<TProps> = ({
  topLine,
  company,
  layout = `desktop`,
  renderImageElement,
  responsiveUnit = `px`,
  responsiveBreakpoints = defaultBreakpoints,
  headingFontSizeRange = defaultHeadingSizes,
  descriptionFontSizeRange = defaultDescriptionSizes,
}) => {
  const { logoUrl, companyNameLabel, description } = company
  const { maxBreakpoint, minBreakpoint } = responsiveBreakpoints

  const hasLogo = Boolean(logoUrl || renderImageElement)
  const hasDescription = description && description.trim().length > 0

  const renderLogoElement = () => {
    // if renderImageElement (such as next/image) was provided, render that
    if (renderImageElement) {
      return renderImageElement(company)
    }

    // otherwise render Chakra Image with the imageUrl
    if (logoUrl) {
      return <Image src={logoUrl} alt={`${companyNameLabel} logo`} />
    }

    // fallback if neither of these two is defined
    return null
  }

  return (
    <Box as={`header`}>
      <Stack direction={`column`} spacing={getStackSpacing(layout)}>
        {topLine}
        <Grid justifyItems={layout === `mobile` ? `center` : `left`} rowGap={`smallMedium`}>
          <Stack
            align={layout === `desktop` ? `start` : `center`}
            direction={layout === `compact` ? `row` : `column`}
          >
            <Box justifySelf={layout === `mobile` ? `center` : `start`}>
              {hasLogo && (
                <StyledLogoBox radius={`small`} className={cn({ small: layout === `mobile` })}>
                  {renderLogoElement()}
                </StyledLogoBox>
              )}
            </Box>
            <Box>
              <H1
                contrast={FG_CONTRAST}
                truncateOnLines={2}
                letterSpacing={letterSpacingValues.tighter}
                textAlign={layout === `mobile` ? `center` : `left`}
                fontStyle={layout === `mobile` ? `bold` : `boldExtra`}
                responsiveSize={{
                  maxBreakpoint,
                  minBreakpoint,
                  unit: responsiveUnit,
                  maxSize: headingFontSizeRange.maxSize,
                  minSize: headingFontSizeRange.minSize,
                }}
              >
                {companyNameLabel}
              </H1>
            </Box>
          </Stack>

          {hasDescription && layout !== `mobile` && (
            <Box>
              <Text
                contrast={FG_CONTRAST}
                fontStyle={`regular`}
                maxWidth={`80ch`}
                truncateOnLines={5}
                textShadow={`medium`}
                title={description}
                responsiveSize={{
                  maxBreakpoint,
                  minBreakpoint,
                  unit: responsiveUnit,
                  maxSize: descriptionFontSizeRange.maxSize,
                  minSize: descriptionFontSizeRange.minSize,
                }}
              >
                {company.isPromoHeaderText && (
                  <Icon
                    iconSize={`20px`}
                    icon={IconMessage}
                    contrast={FG_CONTRAST}
                    marginRight={`0.5rem`}
                    verticalAlign={`text-bottom`}
                  />
                )}
                {description}
              </Text>
            </Box>
          )}
        </Grid>
      </Stack>
    </Box>
  )
}

const getStackSpacing = (layout: THeaderLayout): PropsOf<typeof Stack>['spacing'] => {
  if (layout === `desktop`) {
    return `extraLarge`
  }

  if (layout === `mobile`) {
    return `medium`
  }

  return `large`
}
